import React, { useEffect, useState } from 'react'
import { getCalculatedGpaAverages, getLessonFutureLevelAverage, getOverviewTexts, saveOverviewTexts } from '../../../lib/studentgparigorsummary'
import { convertToFourPointScale } from '../../../utils/gradeUtils'
import { ReportCard, ToastNotification, OverviewTextarea, UpdateButton } from './CourseReportComponents'
import { getHeaderClass, sortLevels } from './CourseReportUtils'
import style from './CourseReportStyles.module.css'

export const CalculatedGPASummary = ({ studentId, isStudent = false }) => {
	const [current, setCurrent] = useState({})
	const [future, setFuture] = useState({})
	const [levels, setLevels] = useState([])
	const [overview, setOverview] = useState({})
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const loadData = async () => {
		const currentRes = await getCalculatedGpaAverages(studentId)
		const futureRes = await getLessonFutureLevelAverage(studentId)
		const overviewRes = await getOverviewTexts(studentId)

		const currentMap = {}
		const futureMap = {}
		const levelSet = new Set()

		currentRes.forEach((item) => {
			const level = item.level === 'Overall' ? 'Ovr' : item.level
			currentMap[level] = convertToFourPointScale(item.average)
			levelSet.add(level)
		})

		futureRes.forEach((item) => {
			const level = item.level === 'Overall' ? 'Ovr' : item.level
			futureMap[level] = convertToFourPointScale(item.average)
			levelSet.add(level)
		})

		const overviewMap = {}
		overviewRes.forEach((item) => {
			overviewMap[item.type] = item.desc
		})

		setCurrent(currentMap)
		setFuture(futureMap)
		setOverview(overviewMap)
		
		const sortedLevels = sortLevels([...levelSet])
		const finalLevels = ['Ovr']
		sortedLevels.forEach((lvl) => {
			if (lvl !== 'Ovr') finalLevels.push(lvl)
		})
		setLevels(finalLevels)
	}

	useEffect(() => {
		loadData()
	}, [studentId])

	const handleOverviewChange = (value) => {
		setOverview((prev) => ({ ...prev, cavgpa: value }))
	}

	const handleUpdate = async () => {
		await saveOverviewTexts(studentId, { cavgpa: overview.cavgpa || '' })
		showToastMessage('Update successful!')
		loadData()
	}

	const getValue = (map, level) => {
		if (level === 'Ovr') {
			const values = levels
				.filter((lvl) => lvl !== 'Ovr')
				.map((lvl) => parseFloat(map[lvl] || 0))
				.filter((val) => val > 0)

			if (values.length === 0) return '0.0'

			const sum = values.reduce((acc, val) => acc + val, 0)
			return (sum / values.length).toFixed(1)
		}

		return map[level] !== undefined ? parseFloat(map[level]).toFixed(1) : '0.0'
	}

	return (
		<ReportCard title="Calculated GPA">
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />

			<div>
				<table className="table table-bordered text-center">
					<thead>
						<tr>
							<th style={{ width: '150px' }}></th>
							{levels.map((lvl) => (
								<th key={lvl} className={style[getHeaderClass(lvl)]}>
									{lvl}
								</th>
							))}
							<th style={{ width: '400px' }}>Overview</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>Current</td>
							{levels.map((lvl) => {
								const val = parseFloat(getValue(current, lvl))
								return (
									<td key={lvl} className={val.toFixed(1) > 0 ? style[getHeaderClass(lvl)] : ''}>
										{val.toFixed(1)}
									</td>
								)
							})}
							<td rowSpan={3}>
								<OverviewTextarea value={overview['cavgpa'] || ''} onChange={handleOverviewChange} rows={6} isStudent={isStudent} />
							</td>
						</tr>
						<tr>
							<td>Future</td>
							{levels.map((lvl) => {
								const val = parseFloat(getValue(future, lvl))
								return (
									<td key={lvl} className={val.toFixed(1) > 0 ? style[getHeaderClass(lvl)] : ''}>
										{val.toFixed(1)}
									</td>
								)
							})}
						</tr>
						<tr>
							<td>Total</td>
							{levels.map((lvl) => {
								if (lvl === 'Ovr') {
									const totalValues = levels
										.filter((l) => l !== 'Ovr')
										.map((l) => {
											const curr = parseFloat(current[l] || 0)
											const fut = parseFloat(future[l] || 0)
											const vals = []
											if (curr > 0) vals.push(curr)
											if (fut > 0) vals.push(fut)
											if (vals.length === 0) return null
											return vals.reduce((a, b) => a + b, 0) / vals.length
										})
										.filter((v) => v !== null)

									const avg = totalValues.length > 0 ? (totalValues.reduce((a, b) => a + b, 0) / totalValues.length).toFixed(1) : '0.0'

									return (
										<td key={lvl} className={avg > 0 ? style[getHeaderClass(lvl)] : ''}>
											{avg}
										</td>
									)
								} else {
									const curr = parseFloat(current[lvl] || 0)
									const fut = parseFloat(future[lvl] || 0)
									const vals = []
									if (curr > 0) vals.push(curr)
									if (fut > 0) vals.push(fut)
									const avg = vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : '0.0'
									return (
										<td key={lvl} className={(avg > 0 ? style[getHeaderClass(lvl)] : '') + ' fw-bold'}>
											{avg}
										</td>
									)
								}
							})}
						</tr>
					</tbody>
				</table>

				<UpdateButton onClick={handleUpdate} isStudent={isStudent} />
			</div>
		</ReportCard>
	)
}
